import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { hapticSuccess } from '../native';
import { Trophy, Loader2, Plus, X, MapPin, Building, Mail, Phone, DollarSign, Award } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_CATEGORIES = ['U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'Adults'];
const GENDERS = ['Boys', 'Girls', 'Mixed'];
const LEVELS = [
  { value: 'district', label: 'District - Local tournaments' },
  { value: 'regional', label: 'Regional - Multi-district' },
  { value: 'state', label: 'State - State-wide championship' },
  { value: 'national', label: 'National - National competition' }
];

export const CreateTournament = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    age_category: '',
    gender: '',
    start_date: '',
    end_date: '',
    entry_fee: '',
    max_teams: '16',
    fields: [],
    level: 'district',
    location: '',
    venue_name: '',
    venue_address: '',
    contact_email: user?.email || '',
    contact_phone: '',
    registration_deadline: '',
    // Prize Pool
    prize_pool_enabled: false,
    prize_pool_total: '',
    prize_1st: '',
    prize_2nd: '',
    prize_3rd: ''
  });
  const [newField, setNewField] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addField = () => {
    if (newField.trim() && !formData.fields.includes(newField.trim())) {
      setFormData({ ...formData, fields: [...formData.fields, newField.trim()] });
      setNewField('');
    }
  };

  const removeField = (field) => {
    setFormData({ ...formData, fields: formData.fields.filter(f => f !== field) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user || (user.role !== 'admin' && user.role !== 'organizer')) {
      toast.error('Only organizers can create tournaments');
      return;
    }

    if (formData.end_date < formData.start_date) {
      toast.error('End date must be on or after the start date');
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        entry_fee: parseFloat(formData.entry_fee),
        max_teams: parseInt(formData.max_teams),
        contact_email: formData.contact_email || null,
        contact_phone: formData.contact_phone || null,
        registration_deadline: formData.registration_deadline || null,
        // Prize Pool
        prize_pool_enabled: formData.prize_pool_enabled,
        prize_pool_total: parseFloat(formData.prize_pool_total) || 0,
        prize_1st: parseFloat(formData.prize_1st) || 0,
        prize_2nd: parseFloat(formData.prize_2nd) || 0,
        prize_3rd: parseFloat(formData.prize_3rd) || 0
      };
      
      const response = await axios.post(`${API}/tournaments`, payload);
      hapticSuccess();
      toast.success('Tournament created successfully!');
      navigate(`/tournaments/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tournament');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="create-tournament-page">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
              <Trophy className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <CardTitle className="text-2xl font-black">CREATE TOURNAMENT</CardTitle>
              <CardDescription>Set up a new flag football tournament</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tournament Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Tournament Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Spring Championship 2025"
                required
                data-testid="tournament-name-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe your tournament..."
                rows={3}
                required
                data-testid="tournament-description-input"
              />
            </div>

            {/* Level Selection */}
            <div className="space-y-2">
              <Label>Tournament Level *</Label>
              <Select 
                value={formData.level} 
                onValueChange={(v) => setFormData({ ...formData, level: v })}
              >
                <SelectTrigger data-testid="level-select">
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map(level => (
                    <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Age & Gender Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Age Category *</Label>
                <Select 
                  value={formData.age_category} 
                  onValueChange={(v) => setFormData({ ...formData, age_category: v })}
                >
                  <SelectTrigger data-testid="age-category-select">
                    <SelectValue placeholder="Select age group" />
                  </SelectTrigger>
                  <SelectContent>
                    {AGE_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Gender *</Label>
                <Select 
                  value={formData.gender} 
                  onValueChange={(v) => setFormData({ ...formData, gender: v })}
                >
                  <SelectTrigger data-testid="gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDERS.map(g => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Location Section */}
            <div className="p-4 rounded-lg border bg-muted/30 space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                Location Details
              </h3>
              
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="location">City, State *</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Dallas, TX"
                    required
                    data-testid="location-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="venue_name">Venue Name</Label>
                  <Input
                    id="venue_name"
                    name="venue_name"
                    value={formData.venue_name}
                    onChange={handleChange}
                    placeholder="City Sports Complex"
                    data-testid="venue-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="venue_address">Venue Address</Label>
                  <Input
                    id="venue_address"
                    name="venue_address"
                    value={formData.venue_address}
                    onChange={handleChange}
                    placeholder="123 Stadium Way, Dallas, TX 75001"
                    data-testid="venue-address-input"
                  />
                </div>
              </div>
            </div>

            {/* Dates Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date *</Label>
                <Input
                  id="start_date"
                  name="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={handleChange}
                  required
                  data-testid="start-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_date">End Date *</Label>
                <Input
                  id="end_date"
                  name="end_date"
                  type="date"
                  min={formData.start_date || undefined}
                  value={formData.end_date}
                  onChange={handleChange}
                  required
                  data-testid="end-date-input"
                />
              </div>
            </div>
            {formData.start_date && formData.end_date && formData.end_date >= formData.start_date && (
              <p className="text-xs text-muted-foreground -mt-4">
                {(() => {
                  const days = Math.round(
                    (new Date(formData.end_date) - new Date(formData.start_date)) / 86400000
                  ) + 1;
                  return days === 1 ? 'Single-day tournament' : `${days}-day tournament`;
                })()}
              </p>
            )}

            {/* Registration Deadline */}
            <div className="space-y-2">
              <Label htmlFor="registration_deadline">Registration Deadline</Label>
              <Input
                id="registration_deadline"
                name="registration_deadline"
                type="date"
                value={formData.registration_deadline}
                onChange={handleChange}
                data-testid="registration-deadline-input"
              />
            </div>

            {/* Entry Fee */}
            <div className="space-y-2">
              <Label htmlFor="entry_fee">Entry Fee ($) *</Label>
              <Input
                id="entry_fee"
                name="entry_fee"
                type="number"
                min="0"
                step="0.01"
                value={formData.entry_fee}
                onChange={handleChange}
                placeholder="50.00"
                required
                data-testid="entry-fee-input"
              />
              <p className="text-xs text-muted-foreground">
                + $10.00 team registration fee will be added at checkout
              </p>
            </div>

            {/* Contact Info */}
            <div className="p-4 rounded-lg border bg-muted/30 space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Mail className="w-4 h-4 text-primary" />
                Contact Information
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="director@email.com"
                    data-testid="contact-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    name="contact_phone"
                    type="tel"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>
            </div>

            {/* Max Teams */}
            <div className="space-y-2">
              <Label htmlFor="max_teams">Max Teams *</Label>
              <Select 
                value={formData.max_teams} 
                onValueChange={(v) => setFormData({ ...formData, max_teams: v })}
              >
                <SelectTrigger data-testid="max-teams-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[4, 8, 16, 32, 64].map(n => (
                    <SelectItem key={n} value={n.toString()}>{n} Teams</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Playing Fields</Label>
              <div className="flex gap-2">
                <Input
                  value={newField}
                  onChange={(e) => setNewField(e.target.value)}
                  placeholder="Field A, Field 1, etc."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addField())}
                  data-testid="field-input"
                />
                <Button type="button" variant="outline" onClick={addField} data-testid="add-field-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.fields.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.fields.map(field => (
                    <span 
                      key={field}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm"
                    >
                      {field}
                      <button 
                        type="button"
                        onClick={() => removeField(field)}
                        className="hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Prize Pool Section */}
            <div className="p-4 rounded-lg border bg-muted/30 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <Award className="w-4 h-4 text-amber-500" />
                  Prize Pool
                </h3>
                <div className="flex items-center gap-2">
                  <Label htmlFor="prize_pool_enabled" className="text-sm">Enable Prize Pool</Label>
                  <Switch
                    id="prize_pool_enabled"
                    checked={formData.prize_pool_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, prize_pool_enabled: checked })}
                    data-testid="prize-pool-toggle"
                  />
                </div>
              </div>
              
              {formData.prize_pool_enabled && (
                <div className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <Label htmlFor="prize_pool_total">Total Prize Pool ($)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="prize_pool_total"
                        name="prize_pool_total"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.prize_pool_total}
                        onChange={handleChange}
                        placeholder="1000.00"
                        className="pl-10"
                        data-testid="prize-pool-total-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="prize_1st" className="flex items-center gap-1">
                        <Trophy className="w-4 h-4 text-amber-500" /> 1st Place
                      </Label>
                      <Input
                        id="prize_1st"
                        name="prize_1st"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.prize_1st}
                        onChange={handleChange}
                        placeholder="500"
                        data-testid="prize-1st-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="prize_2nd" className="flex items-center gap-1">
                        <Trophy className="w-4 h-4 text-gray-400" /> 2nd Place
                      </Label>
                      <Input
                        id="prize_2nd"
                        name="prize_2nd"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.prize_2nd}
                        onChange={handleChange}
                        placeholder="300"
                        data-testid="prize-2nd-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="prize_3rd" className="flex items-center gap-1">
                        <Trophy className="w-4 h-4 text-amber-700" /> 3rd Place
                      </Label>
                      <Input
                        id="prize_3rd"
                        name="prize_3rd"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.prize_3rd}
                        onChange={handleChange}
                        placeholder="200"
                        data-testid="prize-3rd-input"
                      />
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    Prize amounts will be displayed on the tournament page for all participants to see.
                  </p>
                </div>
              )}
            </div>

            {/* Submit */}
            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={loading || !formData.age_category || !formData.gender || !formData.location}
              data-testid="create-tournament-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Tournament'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
